/**
 * Everything the rule editor knows about the rule language, with no Monaco in sight so that it can
 * be tested directly.
 *
 * The service is the authority on *what* exists: `GET /flagdefinition/dictionary` answers with the
 * class fields, the enums and the namespace functions the engine was built with, reflected out of
 * the Java at startup. What it cannot answer is what any of them mean, so the prose lives here -
 * and anything the dictionary grows that has no prose yet is still offered, marked as undocumented,
 * rather than dropped. That is the point: a helper added to the service has to show up in the editor
 * without a frontend release, which is how `db:effectiveInstructorIds` and
 * `db:klassesByEffectiveInstructor` went unnoticed for a whole cycle.
 */

/** Root name to the paths, signatures or constants underneath it, exactly as the service sends it. */
export type RuleDictionary = Record<string, string[]>;

/**
 * What a dictionary key turns out to be, decided from its entries rather than from a list of names
 * kept here - a namespace or an enum added to the service has to classify itself.
 *
 * - `namespace`: entries are method signatures, `startsWithinWeeks(ZonedDateTime, Number)`. Reached
 *   through a colon, `time:startsWithinWeeks(...)`.
 * - `enum`: entries are constant names, `CONFIRMED`. Reached through a dot, `KlassStatus.CONFIRMED`.
 * - `object`: entries are dotted property paths, `deliveryAddress.country.name`.
 */
export type RuleDictionaryKind = 'namespace' | 'enum' | 'object';

export const classifyDictionaryKey = (entries: string[] | undefined): RuleDictionaryKind => {
  if (!entries || entries.length === 0) return 'object';
  if (entries.some((entry) => entry.includes('('))) return 'namespace';
  if (entries.every((entry) => /^[A-Z][A-Z0-9_]*$/.test(entry))) return 'enum';
  return 'object';
};

// ==========================================
// SIGNATURES
// ==========================================

export interface RuleFunctionSignature {
  name: string;
  /** Java types, the only thing the dictionary knows about the parameters. */
  paramTypes: string[];
  /** The dictionary's own text, e.g. `hasRecord(String, String, Object)`. */
  raw: string;
}

export const parseSignature = (raw: string): RuleFunctionSignature | null => {
  const match = raw.match(/^([A-Za-z_][A-Za-z0-9_]*)\((.*)\)$/);
  const name = match?.[1];
  if (!name) return null;

  const params = (match?.[2] ?? '').trim();
  return {
    name,
    paramTypes: params ? params.split(',').map((type) => type.trim()) : [],
    raw
  };
};

/** Every overload of one function, longest arity last. */
export const signaturesOf = (
  dictionary: RuleDictionary,
  namespace: string,
  name: string
): RuleFunctionSignature[] =>
  (dictionary[namespace] ?? [])
    .map(parseSignature)
    .filter((signature): signature is RuleFunctionSignature => signature?.name === name)
    .sort((a, b) => a.paramTypes.length - b.paramTypes.length);

// ==========================================
// THE PROSE
// ==========================================

export interface RuleFunctionParam {
  name: string;
  description: string;
}

export interface RuleFunctionDoc {
  summary: string;
  /**
   * Positional, written out for the longest overload. A shorter overload takes a prefix of this
   * list, which is what the families are shaped like: `coalesce(a)` through `coalesce(a, b, c, d)`,
   * `hasRecord` from one field-and-value pair up to four.
   */
  params: RuleFunctionParam[];
  returns: string;
  example: string;
  /** Anything about the function that is a trap rather than a description. */
  note?: string;
}

/** Keyed `namespace:function`, so two namespaces may both have a `coalesce` without colliding. */
export const FUNCTION_DOCS: Record<string, RuleFunctionDoc> = {
  'time:startsWithinWeeks': {
    summary:
      'True when a date falls between today and the given number of weeks from now. A date in the past is false, and so is a null date - which is what makes it safe on a class whose dates were never set.',
    params: [
      { name: 'targetDate', description: 'The date to test, usually `klass.startDateTime`.' },
      { name: 'weeks', description: 'How many weeks ahead the window reaches. `2` means the next fortnight.' }
    ],
    returns: 'Boolean',
    example: '// The class starts inside the next three weeks\ntime:startsWithinWeeks(klass.startDateTime, 3)'
  },
  'time:isOverlapping': {
    summary:
      'True when two time windows intersect at any point. Touching windows do not count: a class that ends exactly when another starts is not an overlap.',
    params: [
      { name: 'start1', description: 'Start of the first window.' },
      { name: 'end1', description: 'End of the first window.' },
      { name: 'start2', description: 'Start of the second window.' },
      { name: 'end2', description: 'End of the second window.' }
    ],
    returns: 'Boolean',
    example:
      'time:isOverlapping(klass.startDateTime, klass.endDateTime,\n                   other.startDateTime, other.endDateTime)',
    note: 'False if any of the four is null, so a class with no end date never reports a clash.'
  },
  'db:coalesce': {
    summary:
      'The first argument that is set - neither null nor blank. For fallback chains, where the value you want is whichever of several fields was filled in.',
    params: [
      { name: 'first', description: 'Checked first; returned if it is set.' },
      { name: 'second', description: 'Used when the first is null or blank.' },
      { name: 'third', description: 'Used when neither of the first two is set.' },
      { name: 'fourth', description: 'Last fallback.' }
    ],
    returns: 'The first value that is set, or null when none of them is',
    example: "var contact = db:coalesce(klass.spocEmail, klass.facilityContactEmail);",
    note: 'Blank counts as unset: a field holding only spaces is skipped like a null one.'
  },
  'db:effectiveInstructorIds': {
    summary:
      'The instructors actually teaching a class, as ids, resolved through the precedence chain the rest of the service uses: the confirmed ones if the class has any, otherwise the allocated ones, otherwise the ones requested. A class can have several, so a rule about "the instructor" has to loop.',
    params: [{ name: 'klassId', description: 'The class to resolve, normally `klass.id`.' }],
    returns: 'A list of instructor ids, empty rather than null',
    example:
      'for (var instructorId : db:effectiveInstructorIds(klass.id)) {\n    // one iteration per instructor on this class\n}'
  },
  'db:klassesByEffectiveInstructor': {
    summary:
      'The other live classes an instructor is teaching. Resolves the same precedence chain as `db:effectiveInstructorIds`, so a class the instructor was replaced on is not reported as a clash, and leaves out anything cancelled or delivered.',
    params: [
      { name: 'instructorId', description: 'The instructor to look up, e.g. from `db:effectiveInstructorIds`.' },
      { name: 'excludeKlassId', description: 'Kept out of the results - pass `klass.id` so the class does not clash with itself.' }
    ],
    returns: 'A list of classes, each with the same fields as `klass`',
    example:
      'var others = db:klassesByEffectiveInstructor(instructorId, klass.id);\nfor (var other : others) {\n    // compare dates, facility, whatever the rule is about\n}'
  },
  'db:fetchRelated': {
    summary:
      'Records whose given columns hold a particular id. The general-purpose lookup, for relationships that have no helper of their own; on `Klass` it excludes cancelled and delivered classes.',
    params: [
      { name: 'entityName', description: "The entity to search, as the service names it: `'Klass'`, `'PrivateKlass'`, `'AutomationMessage'`." },
      { name: 'referenceFields', description: "Comma-separated columns to match the id against, e.g. `'clientExecutive.id, tss.id'`." },
      { name: 'targetId', description: 'The id being looked for.' },
      { name: 'excludeId', description: 'One id to leave out of the results, or null.' }
    ],
    returns: 'A list of records',
    example: "var sameExecutive = db:fetchRelated('Klass', 'clientExecutive.id', klass.clientExecutive.id, klass.id);",
    note:
      'For instructor conflicts use `db:klassesByEffectiveInstructor` instead. The singular instructor columns this used to be pointed at no longer exist, and a field name that does not resolve fails the whole rule.'
  },
  'db:hasRecord': {
    summary:
      'Whether any record of an entity matches every field-and-value pair given. One to four pairs, and all of them have to match.',
    params: [
      { name: 'entityName', description: "The entity to search, as the service names it: `'AutomationMessage'`, `'PrivateKlass'`." },
      { name: 'field1', description: 'Name of the first column to match.' },
      { name: 'value1', description: 'Value the first column has to hold.' },
      { name: 'field2', description: 'Second column.' },
      { name: 'value2', description: 'Value for the second column.' },
      { name: 'field3', description: 'Third column.' },
      { name: 'value3', description: 'Value for the third column.' },
      { name: 'field4', description: 'Fourth column.' },
      { name: 'value4', description: 'Value for the fourth column.' }
    ],
    returns: 'Boolean',
    example: "db:hasRecord('AutomationMessage', 'entity', 'PrivateKlass', 'key', '' + klass.requestId)",
    note:
      "Values match exactly, and a number is not a string: prefix with `'' + ` when the column holds text, as the key column does."
  }
};

/** What a namespace is for, shown when it is offered as a root. */
export const NAMESPACE_DOCS: Record<string, string> = {
  time: 'Date and time helpers. Called with a colon: `time:startsWithinWeeks(klass.startDateTime, 2)`.',
  db: 'Lookups against the database, and the fallback helper. Called with a colon: `db:hasRecord(...)`.'
};

/** What a root data object is, shown when it is offered as a root. */
export const OBJECT_DOCS: Record<string, string> = {
  klass: 'The class being evaluated. Every rule is about this one object; dot into it for its fields.'
};

/** The field each enum is meant to be compared with, so the completion can show a usable example. */
export const ENUM_FIELDS: Record<string, string> = {
  KlassStatus: 'klass.status',
  KlassType: 'klass.type',
  KlassModality: 'klass.modality',
  KlassHardware: 'klass.hardware',
  KlassDecision: 'klass.decision'
};

/**
 * Notes on the fields that are read wrongly more often than not, keyed by full path. Everything
 * else is offered with its path and nothing more - the dictionary carries no types, and inventing
 * descriptions for 237 paths here would rot faster than it helps.
 */
export const FIELD_NOTES: Record<string, string> = {
  'klass.id': 'The class id. What the `db:` helpers take.',
  'klass.instructors':
    'The instructors **requested** for the class - a collection, not a single instructor. Test it with `empty(klass.instructors)` or `size(klass.instructors)`.',
  'klass.allocatedInstructors':
    'The instructors **planned** for the class - a collection. Set once allocation has run, and superseded by `finalInstructors` once they are confirmed.',
  'klass.finalInstructors':
    'The instructors **confirmed** to teach the class - a collection, and the top of the precedence chain. `db:effectiveInstructorIds(klass.id)` resolves that chain for you.',
  'klass.status': 'Compare with the constants: `klass.status == KlassStatus.CONFIRMED`.',
  'klass.type': 'Compare with the constants: `klass.type == KlassType.PRIVATE`.',
  'klass.modality': 'Compare with the constants: `klass.modality == KlassModality.VIRTUAL`.',
  'klass.hardware': 'Compare with the constants: `klass.hardware == KlassHardware.BYOD`.',
  'klass.decision': 'Compare with the constants: `klass.decision == KlassDecision.CONFIRMED`.',
  'klass.startDateTime': 'Start of the class, with a zone. Use the `time:` helpers rather than comparing it by hand.',
  'klass.endDateTime': 'End of the class, with a zone. Pairs with `startDateTime` in `time:isOverlapping`.',
  'klass.activeFlags': 'The flags currently raised on the class - a collection. Useful for rules that only apply once another flag is up.',
  'klass.totalEnrolled': 'Everyone booked on the class, however they were booked. Null on a class no one has enrolled on yet.',
  'klass.course': 'The catalogue course. Null on a class imported without one, which `klass.course == null` is the way to catch.',
  // Both of the following exist because the field is a trap for exactly the rule somebody would
  // reach for it to write. The completion paths come from the service by reflection, so both fields
  // are offered in this editor whether they are described here or not - which is why describing
  // them is the cheap fix for a rule nobody has written yet.
  'klass.deliveryAddressText':
    'The delivery address **as upstream sent it**, present on every imported private class. **Not a failure marker** - it does not mean the address could not be read, though it once did. `klass.deliveryAddressText != null` therefore matches every private class. For "the address could not be read", test the address instead: this is set and `klass.deliveryAddress` has no lines. Better still, use `db:hasInconsistency(klass)` - the import already raises an automation message for one it could not read.',
  'klass.deliveryAddress':
    'The address **read out of** `deliveryAddressText`. `postcode` is legitimately null where no code in the address matched one the country has - the service leaves it empty rather than guessing - so a rule requiring a postcode will fire on correct addresses, and on every address in a country the reference data does not cover. See Administration &rsaquo; Postal Codes for which countries those are.'
};

/**
 * A `BigDecimal` reflected field by field. The service walks into `java.math` the way it walks into
 * any other object, so `klass.revenue` arrives carrying the internals of the number - and offering
 * `klass.revenue.intVal.mag` to someone writing a rule about money is worse than offering nothing.
 * A field whose children are these is treated as the scalar it is.
 *
 * The real fix is in `RuleDictionaryService.getFieldsRecursive`, which already skips `java.lang`,
 * `java.time` and `java.util` and should skip `java.math` too.
 */
const DECIMAL_INTERNALS = ['intCompact', 'intVal', 'precision', 'scale', 'stringCache'];

const isDecimalInternals = (names: string[]): boolean =>
  DECIMAL_INTERNALS.every((internal) => names.includes(internal));

// ==========================================
// SUGGESTIONS
// ==========================================

export type RuleSuggestionKind =
  | 'namespace'
  | 'function'
  | 'object'
  | 'field'
  | 'enum'
  | 'enumMember'
  | 'variable'
  | 'keyword';

/**
 * One entry of the completion list, in terms the editor layer turns into a Monaco item. Kept free
 * of Monaco's own types so the decisions above can be asserted on without a DOM.
 */
export interface RuleSuggestion {
  label: string;
  insertText: string;
  /** `insertText` carries `${1:placeholder}` snippet stops. */
  isSnippet?: boolean;
  kind: RuleSuggestionKind;
  /** The grey text beside the label. The service's own signature, where there is one. */
  detail: string;
  /** Markdown, shown in the details pane beside the list. */
  documentation?: string;
  sortText?: string;
  /** Overrides what Monaco matches the typed text against - needed when the insertion reaches back
   *  over a character the user already typed. */
  filterText?: string;
  /** How many characters before the current word the insertion replaces. */
  replaceCharsBefore?: number;
  /** Open the suggest widget again once this one is accepted, because it only started a path. */
  retrigger?: boolean;
}

/**
 * `db:hasRe`, `time:` - a namespace call. The guard in front stops it matching the tail of a path
 * like `klass.deliveryAddress.city`, and every match is still checked against the dictionary, so
 * `'entity:x'` inside a string literal suggests nothing.
 */
const NAMESPACE_CALL = /(?:^|[^A-Za-z0-9_.])([A-Za-z_][A-Za-z0-9_]*):([A-Za-z0-9_]*)$/;

/** `klass.`, `klass.deliveryAddress.ci` - a property path, greedy so the deepest prefix wins. */
const PROPERTY_PATH = /([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z0-9_]+)*)\.([A-Za-z0-9_]*)$/;

/**
 * What to offer at the cursor, given the text of the current line up to it.
 *
 * Roots are matched exactly, not case-insensitively as this used to: JEXL resolves `klass` and
 * nothing else, so completing `Klass.` with fields that can never resolve only buys a rule that
 * fails at evaluation - and the syntax check under the editor does not look at names.
 */
export const buildRuleSuggestions = (
  dictionary: RuleDictionary,
  textBeforeCursor: string
): RuleSuggestion[] => {
  const namespace = textBeforeCursor.match(NAMESPACE_CALL)?.[1];
  const namespaceEntries = namespace ? dictionary[namespace] : undefined;
  if (namespace && namespaceEntries && classifyDictionaryKey(namespaceEntries) === 'namespace') {
    return functionSuggestions(namespace, namespaceEntries, 0);
  }

  const path = textBeforeCursor.match(PROPERTY_PATH)?.[1];
  if (path) {
    return pathSuggestions(dictionary, path);
  }

  return rootSuggestions(dictionary);
};

const pathSuggestions = (dictionary: RuleDictionary, typedPath: string): RuleSuggestion[] => {
  const [root, ...rest] = typedPath.split('.');
  const entries = root ? dictionary[root] : undefined;
  if (!root || !entries) return [];

  switch (classifyDictionaryKey(entries)) {
    case 'namespace':
      // `db.coalesce(...)` parses and then fails at evaluation, because a namespace is not a
      // variable in the context. Rather than leaving the list empty at the moment someone has
      // guessed wrong, the functions are offered with the dot itself included in what the
      // insertion replaces, so accepting one rewrites `db.` into `db:`.
      return rest.length ? [] : functionSuggestions(root, entries, 1);
    case 'enum':
      return rest.length ? [] : enumMemberSuggestions(root, entries);
    default:
      return fieldSuggestions(root, entries, rest);
  }
};

const functionSuggestions = (
  namespace: string,
  entries: string[],
  replaceCharsBefore: number
): RuleSuggestion[] =>
  entries
    .map(parseSignature)
    .filter((signature): signature is RuleFunctionSignature => signature !== null)
    .map((signature) => {
      const described = describeFunction(namespace, signature);
      const colon = replaceCharsBefore > 0 ? ':' : '';

      return {
        label: described.label,
        insertText: colon + described.snippet,
        isSnippet: true,
        kind: 'function' as RuleSuggestionKind,
        detail: `${namespace}:${signature.raw}`,
        documentation: described.documentation,
        // Overloads of one function, shortest first, and all of them under the function's name so
        // the four `coalesce`s stay together.
        sortText: `${signature.name}${String(signature.paramTypes.length).padStart(2, '0')}`,
        // The typed text the insertion replaces starts with the dot being corrected, so that is
        // what Monaco has to match against.
        filterText: replaceCharsBefore > 0 ? `.${signature.name}` : undefined,
        replaceCharsBefore
      };
    });

const enumMemberSuggestions = (enumName: string, values: string[]): RuleSuggestion[] =>
  values.map((value) => {
    const field = ENUM_FIELDS[enumName];

    return {
      label: value,
      insertText: value,
      kind: 'enumMember',
      detail: `${enumName} constant`,
      documentation: field
        ? `Compare it with the field it belongs to:\n\n\`\`\`javascript\n${field} == ${enumName}.${value}\n\`\`\``
        : `A constant of \`${enumName}\`. Compare it with the field it belongs to rather than with the quoted name.`
    };
  });

/**
 * The immediate property names under `prefix`, each mapped to the names one level below it - which
 * is all that is needed to tell a leaf from something worth dotting into.
 */
const immediateChildren = (entries: string[], prefix: string): Map<string, string[]> => {
  const children = new Map<string, string[]>();

  for (const entry of entries) {
    if (prefix && !entry.startsWith(prefix)) continue;

    const [child, grandchild] = entry.slice(prefix.length).split('.');
    if (!child) continue;

    const grandchildren = children.get(child) ?? [];
    if (grandchild) grandchildren.push(grandchild);
    children.set(child, grandchildren);
  }

  return children;
};

const fieldSuggestions = (root: string, entries: string[], rest: string[]): RuleSuggestion[] => {
  const prefix = rest.length ? `${rest.join('.')}.` : '';
  const children = immediateChildren(entries, prefix);

  // The path itself is a number, not an object: nothing under it is worth offering.
  if (isDecimalInternals([...children.keys()])) return [];

  return [...children.entries()].map(([name, grandchildren]) => {
    const path = `${root}.${prefix}${name}`;
    const isObject = grandchildren.length > 0 && !isDecimalInternals(grandchildren);
    const note = FIELD_NOTES[path];

    return {
      label: name,
      insertText: name,
      kind: isObject ? 'object' : 'field',
      detail: path,
      documentation: note ?? (isObject ? 'Holds fields of its own - keep typing after the dot.' : undefined)
    };
  });
};

/**
 * JEXL's own vocabulary. Not in the dictionary, which describes the data rather than the language,
 * and the shapes people get wrong from memory - `for (var x : list)` is not the JavaScript the
 * editor's highlighting suggests it is.
 */
const KEYWORD_SUGGESTIONS: RuleSuggestion[] = [
  {
    label: 'var',
    insertText: 'var ${1:name} = ${2:value};',
    isSnippet: true,
    kind: 'keyword',
    detail: 'declare a working variable',
    documentation: 'Rules may be several statements long. The **last expression** is still the answer.'
  },
  {
    label: 'if',
    insertText: 'if (${1:condition}) {\n\t$0\n}',
    isSnippet: true,
    kind: 'keyword',
    detail: 'conditional',
    documentation: '`if`, `else if` and `else` all work, with braces.'
  },
  {
    label: 'for',
    insertText: 'for (var ${1:item} : ${2:list}) {\n\t$0\n}',
    isSnippet: true,
    kind: 'keyword',
    detail: 'iterate a collection',
    documentation:
      'A colon, not `of` or `in`:\n\n```javascript\nfor (var instructorId : db:effectiveInstructorIds(klass.id)) {\n}\n```'
  },
  {
    label: 'empty',
    insertText: 'empty(${1:value})',
    isSnippet: true,
    kind: 'keyword',
    detail: 'null, blank or nothing in it',
    documentation:
      'True for null, for an empty string and for an empty collection - the safe way to ask whether a class has instructors:\n\n```javascript\nempty(klass.instructors) && empty(klass.allocatedInstructors)\n```'
  },
  {
    label: 'size',
    insertText: 'size(${1:value})',
    isSnippet: true,
    kind: 'keyword',
    detail: 'how many',
    documentation: 'Items in a collection, or characters in a string.'
  },
  { label: 'null', insertText: 'null', kind: 'keyword', detail: 'no value' },
  { label: 'true', insertText: 'true', kind: 'keyword', detail: 'raise the flag' },
  { label: 'false', insertText: 'false', kind: 'keyword', detail: 'do not raise the flag' }
];

const rootSuggestions = (dictionary: RuleDictionary): RuleSuggestion[] => {
  const roots = Object.entries(dictionary).map(([key, entries]): RuleSuggestion => {
    switch (classifyDictionaryKey(entries)) {
      case 'namespace':
        return {
          label: key,
          // The colon is the whole difficulty with these, so the completion types it.
          insertText: `${key}:`,
          kind: 'namespace',
          detail: `${entries.length} functions, called as ${key}:`,
          documentation: NAMESPACE_DOCS[key] ?? `Functions, called with a colon: \`${key}:someFunction(...)\`.`,
          sortText: `2${key}`,
          retrigger: true
        };
      case 'enum':
        return {
          label: key,
          insertText: `${key}.`,
          kind: 'enum',
          detail: `${entries.length} constants`,
          documentation: ENUM_FIELDS[key]
            ? `The values \`${ENUM_FIELDS[key]}\` can hold.`
            : `Constants: ${entries.map((value) => `\`${value}\``).join(', ')}.`,
          sortText: `3${key}`,
          retrigger: true
        };
      default:
        return {
          label: key,
          insertText: key,
          kind: 'variable',
          detail: `${entries.length} fields`,
          documentation: OBJECT_DOCS[key],
          // The data comes first: it is what nearly every rule starts with.
          sortText: `1${key}`
        };
    }
  });

  return [...roots, ...KEYWORD_SUGGESTIONS.map((keyword) => ({ ...keyword, sortText: `4${keyword.label}` }))];
};

// ==========================================
// SIGNATURE HELP
// ==========================================

export interface ActiveCall {
  namespace: string;
  name: string;
  /** 0 based index of the argument the cursor sits in. */
  activeParameter: number;
}

/**
 * The namespace call the cursor is inside, for the parameter hint shown while typing arguments.
 *
 * Scans forward keeping a stack rather than backwards from the cursor, because that is what makes
 * nesting - `db:hasRecord('X', 'k', '' + size(klass.instructors))` - and a call spread over several
 * lines come out right. String literals and `//` comments are stepped over, so a comma or a bracket
 * inside either does not count.
 */
export const findActiveCall = (textBeforeCursor: string): ActiveCall | null => {
  const stack: { namespace: string; name: string; args: number }[] = [];
  let quote: string | null = null;

  for (let i = 0; i < textBeforeCursor.length; i++) {
    const char = textBeforeCursor[i];

    if (quote) {
      if (char === quote) quote = null;
      continue;
    }
    if (char === "'" || char === '"') {
      quote = char;
      continue;
    }
    if (char === '/' && textBeforeCursor[i + 1] === '/') {
      const lineEnd = textBeforeCursor.indexOf('\n', i);
      if (lineEnd === -1) break;
      i = lineEnd;
      continue;
    }
    if (char === '(') {
      const called = textBeforeCursor.slice(0, i).match(/([A-Za-z_][A-Za-z0-9_]*):([A-Za-z_][A-Za-z0-9_]*)$/);
      stack.push({ namespace: called?.[1] ?? '', name: called?.[2] ?? '', args: 0 });
      continue;
    }
    if (char === ')') {
      stack.pop();
      continue;
    }
    if (char === ',') {
      const open = stack[stack.length - 1];
      if (open) open.args++;
    }
  }

  const innermost = stack[stack.length - 1];
  if (!innermost?.namespace) return null;

  return { namespace: innermost.namespace, name: innermost.name, activeParameter: innermost.args };
};

// ==========================================
// RENDERING
// ==========================================

export interface DescribedFunction {
  /** `hasRecord(entityName, field1, value1)` - names, because the types are in the detail line. */
  label: string;
  /** Insertion with a snippet stop per parameter. */
  snippet: string;
  documentation: string;
  params: { name: string; type: string; description: string }[];
}

/**
 * One dictionary signature written out for a human: the prose above where there is any, and an
 * honest notice where there is not - a function the service has grown is still offered.
 */
export const describeFunction = (
  namespace: string,
  signature: RuleFunctionSignature
): DescribedFunction => {
  const doc = FUNCTION_DOCS[`${namespace}:${signature.name}`];
  const params = signature.paramTypes.map((type, index) => ({
    type,
    name: doc?.params[index]?.name ?? `arg${index + 1}`,
    description: doc?.params[index]?.description ?? ''
  }));

  const label = `${signature.name}(${params.map((param) => param.name).join(', ')})`;
  const snippet = params.length
    ? `${signature.name}(${params.map((param, index) => `\${${index + 1}:${param.name}}`).join(', ')})`
    : `${signature.name}()`;

  const documentation = [
    doc?.summary ??
      `Straight from the service dictionary, with no description recorded here yet. It takes ${
        signature.paramTypes.length
      } argument${signature.paramTypes.length === 1 ? '' : 's'}${
        signature.paramTypes.length ? ` of type \`${signature.paramTypes.join(', ')}\`` : ''
      }.`,
    ...params
      .filter((param) => param.description)
      .map((param) => `- \`${param.name}\` *(${param.type})* — ${param.description}`),
    doc?.returns ? `Returns **${doc.returns}**.` : '',
    doc?.note ? `> ${doc.note}` : '',
    doc?.example ? `\`\`\`javascript\n${doc.example}\n\`\`\`` : ''
  ]
    .filter(Boolean)
    .join('\n\n');

  return { label, snippet, documentation, params };
};
